import { useCallback, useState } from "react";
import useSWR from "swr";

import {
  createProvisioning,
  createProvisioningBatch,
  listProvisionings,
  PROVISIONINGS_KEY,
  type CreateProvisioningPayload,
} from "@/features/provision-stocks/api/provisioningApi";
import { PROVISION_STOCKS_KEY } from "@/features/provision-stocks/api/provisionStocksApi";
import { revalidate, toErrorMessage } from "@/lib/swr";

const EMPTY: unknown[] = [];

export function useProvisionings<T = unknown>() {
  const { data, error, isLoading, mutate } = useSWR(PROVISIONINGS_KEY, listProvisionings<T>);

  return {
    provisionings: (data ?? EMPTY) as T[],
    isLoading,
    error: toErrorMessage(error),
    refresh: mutate,
  };
}

export function useCreateProvisioning() {
  const { mutate } = useSWR(PROVISIONINGS_KEY, listProvisionings);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async <R>(action: () => Promise<R>): Promise<R> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const result = await action();
        await Promise.all([mutate(), revalidate(PROVISION_STOCKS_KEY)]);
        return result;
      } catch (e) {
        setError(toErrorMessage(e));
        throw e;
      } finally {
        setIsSubmitting(false);
      }
    },
    [mutate],
  );

  const create = useCallback((payload: CreateProvisioningPayload) => run(() => createProvisioning(payload)), [run]);

  const createBatch = useCallback(
    (payloads: CreateProvisioningPayload[]) => run(() => createProvisioningBatch(payloads)),
    [run],
  );

  return { create, createBatch, isSubmitting, error };
}
